<template>
  <div class="wfh-modal-mask">
    <div class="wfh-modal-wrapper">
      <div class="wfh-modal-content">
        <div class="row">
          <button type="button" @click="closeModal" class="modal-close-btn">
            X
          </button>
        </div>

        <form @submit.prevent="submitModal">
          <div class="row mb-4">
            <div class="col wfh-modal-header-content">
              <div class="m-header">Add Custom Field</div>
            </div>
          </div>

          <div class="form-group">
            <label class="wfh-label">Field Name</label>
            <input
              type="text"
              class="
                form-control
                wfh-input-border wfh-input-padding wfh-input-text
              "
              v-model="label"
              required
              :disabled="isDisabled"
            />
          </div>

          <div class="form-group">
            <label class="wfh-label">Field Type</label>
            <div class="wfh-v-select-override">
              <v-select
                :disabled="isDisabled"
                :options="fieldOptions"
                :filterable="false"
                :clearable="false"
                v-model="type"
                :reduce="(item) => item.value"
              >
              </v-select>
            </div>
          </div>

          <div class="my-4" v-if="hasOptions">
            <div class="wfh-modal-fixed-height pl-3" ref="options-list">
              <div v-for="(option, key) of options">
                <div class="form-group">
                  <label class="wfh-label">Option #{{ key + 1 }}</label>
                  <input
                    ref="option"
                    type="text"
                    class="
                      form-control
                      wfh-input-border wfh-input-padding wfh-input-text
                    "
                    v-model="option.label"
                    @keydown.enter.prevent="addOption"
                    required
                    :disabled="isDisabled"
                  />
                </div>

                <div class="row no-gutters" v-if="!isDisabled">
                  <div class="col-auto ml-auto">
                    <button
                      type="button"
                      class="btn wfh-btn-link-secondary font-weight-bold pr-0"
                      @click="removeOption(key)"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="row no-gutters" v-if="!isDisabled">
              <div class="col-auto ml-auto">
                <button
                  type="button"
                  class="btn wfh-btn-link-primary font-weight-bold pr-0"
                  @click="addOption"
                >
                  Add More Option
                </button>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <div class="d-flex">
                <label class="wfh-label mr-1">Searchable</label>
                <checkbox
                  v-model="searchable"
                  :type="checkboxType"
                  @clicked="
                    searchable = !searchable;
                    updateField();
                  "
                ></checkbox>
              </div>
            </div>
          </div>

          <div class="row mt-4 mb-1">
            <div :class="actionButtonClass" v-if="!isNewField">
              <button
                class="btn btn-block wfh-btn-color-secondary wfh-btn-large"
                @click="deleteField"
              >
                Delete
              </button>
            </div>
            <div :class="actionButtonClass" v-if="!isDisabled">
              <button
                type="submit"
                class="btn btn-block wfh-btn-color-primary wfh-btn-large"
              >
                Apply
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select"

export default {
  components: { vSelect },

  props: {
    selectedCustomField: {
      required: false,
      type: Object,
    },
  },

  data() {
    return {
      searchable: this.selectedCustomField?.searchable ?? false,
      value: this.selectedCustomField?.value ?? "",
      type: this.selectedCustomField?.type ?? "text",
      label: this.selectedCustomField?.label ?? "",
      options: this.selectedCustomField?.options ?? [],
      fieldOptions: [
        {
          label: "Text Field",
          value: "text",
        },
        {
          label: "Date",
          value: "date",
        },
        {
          label: "Dropdown",
          value: "dropdown",
        },
      ],
    }
  },

  computed: {
    checkboxType() {
      return this.searchable ? "primary" : ""
    },

    hasOptions() {
      return this.type === "dropdown"
    },

    isNewField() {
      return (
        this.selectedCustomField === null ||
        typeof this.selectedCustomField === "undefined"
      )
    },

    actionButtonClass() {
      return {
        "col-12": true,
        "col-md-6": !this.isNewField || !this.isDisabled,
        "col-md-5": this.isNewField || this.isDisabled,
        "mx-auto": this.isNewField || this.isDisabled,
      }
    },

    isDisabled() {
      return (
        this.selectedCustomField !== null &&
        this.selectedCustomField.hasOwnProperty("id")
      )
    },
  },

  methods: {
    addOption() {
      this.options.push({ value: "", label: "" })

      this.$nextTick(() => {
        this.$refs["options-list"].scrollTop =
          this.$refs["options-list"].scrollHeight + 80
        this.$refs["option"][this.options.length - 1].focus()
      })
    },

    removeOption(index) {
      this.options.splice(index, 1)
    },

    submitModal() {
      const requiredFields = ["label"]

      for (const requiredField of requiredFields) {
        if (!this[requiredField]) {
          return
        }
      }

      this.generateOptionSlugs()

      const customField = {
        type: this.type,
        value: this.value,
        label: this.label,
        searchable: this.searchable,
        options: this.options,
      }

      this.$emit("submit", customField)
    },

    deleteField() {
      this.$emit("delete")
    },

    updateField() {
      if (this.selectedCustomField?.id) {
        const customField = {
          id: this.selectedCustomField.id,
          label: this.selectedCustomField.label,
          value: this.value,
          searchable: this.searchable,
        }

        this.$emit("update", customField)
      }
    },

    generateOptionSlugs() {
      this.options = this.options.map((option) => {
        option.value = option.label
          .toLowerCase()
          .replace(/[^\w ]+/g, "")
          .replace(/ +/g, "-")

        return option
      })
    },

    closeModal() {
      this.$emit("close")
    },
  },

  mounted() {
    document.body.style.position = "fixed"
    document.body.style.top = `-${window.scrollY}px`
  },

  beforeDestroy() {
    const scrollY = document.body.style.top

    document.body.style.position = ""
    document.body.style.top = ""
    window.scrollTo(0, parseInt(scrollY || "0") * -1)
  },
};
</script>
